import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { CookiesKey } from "@/app/utils/cookies";
import Cookies from "js-cookie";
import { getAppCheckToken } from '@/app/firebase';

class ApiRequest {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: "https://back.dana-assist.com/api/v1",
      headers: {
        'Content-Type': 'application/json',
        'X-App-Version': '1.1.1',
      },
    });

    this.client.interceptors.request.use(
      async (config) => {
        const token = Cookies.get(CookiesKey.token);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (config.url?.includes('sendOtp')) {
          const appCheckToken = await getAppCheckToken();
          if (appCheckToken) {
            config.headers['X-Firebase-AppCheck'] = appCheckToken;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, {
    query,
    config,
  }: {
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const token = Cookies.get(CookiesKey.token);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response: AxiosResponse<T> = await this.client.get(url, {
        ...config,
        params: query,
        url: "",
        headers: {
          ...config?.headers,
          ...headers,
        },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          if (error.response.status === 401) {
            Cookies.remove(CookiesKey.token);
            window.location.href = '/';
          }
          throw { ...error.response.data, httpStatus: error.response.status };
        }
      }
      this.handleError(error);
      throw error;
    }
  }

  // POST method with token handling already implemented
  async post<T>(url: string, {
    data,
    query,
    config,
  }: {
    data?: any;
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.post(url, data, {
        ...config,
        params: query,
      });
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        this.handleError(error);
        if (error.response) {
          if (error.response.status === 401) {
            Cookies.remove(CookiesKey.token);
            window.location.href = '/';
          }
          throw { ...error.response.data, httpStatus: error.response.status };
        }
      }
      throw error;
    }
  }

  // PUT method to update data on the server
  async put<T>(url: string, {
    data,
    query,
    config,
  }: {
    data?: any;
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.put(url, data, {
        ...config,
        params: query,
      });
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        this.handleError(error);
        if (error.response) {
          if (error.response.status === 401) {
            Cookies.remove(CookiesKey.token);
            window.location.href = '/';
          }
          throw { ...error.response.data, httpStatus: error.response.status };
        }
      }
      throw error;
    }
  }

  // DELETE method to remove data on the server
  async delete<T>(url: string, {
    query,
    config,
  }: {
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.delete(url, {
        ...config,
        params: query,
      });
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        this.handleError(error);
        if (error.response) {
          if (error.response.status === 401) {
            Cookies.remove(CookiesKey.token);
            window.location.href = '/';
          }
          throw { ...error.response.data, httpStatus: error.response.status };
        }
      }
      throw error;
    }
  }

  // Handles and logs API errors
  private handleError(error: any): void {
    console.error('API Error:', error);
  }
}

const request = new ApiRequest();
export default request;
